// src/components/ProtectedRoute.tsx
import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { refreshAccessToken } from "@/lib/services/auth";
import { ACCESS_TOKEN_KEY } from "@/lib/constants";

interface ProtectedRouteProps {
  children: JSX.Element;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();

  // Fast path: token already in localStorage — render immediately.
  // The axios interceptor will silently refresh it if it's expired.
  const hasToken = Boolean(localStorage.getItem(ACCESS_TOKEN_KEY));

  const [status, setStatus] = useState<"ok" | "checking" | "denied">(
    hasToken ? "ok" : "checking"
  );

  useEffect(() => {
    if (hasToken) return;

    // Token missing — try to restore session via the HttpOnly refresh cookie.
    // This covers: cleared localStorage, new browser tab, cache wipe, etc.
    refreshAccessToken()
      .then(() => setStatus("ok"))
      .catch(() => setStatus("denied"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === "checking") return null; // brief invisible pause while checking
  if (status === "denied") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}
